import React, {
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import PropTypes from "prop-types";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useClickOutside } from "hooks/useClickOutside";
import { useDatepickerPopoverPosition } from "./useDatepickerPopoverPosition";
import {
	Button,
	TextField
} from "@shopify/polaris";
import {
	applyDateSlotEdit,
	areDateSlotsComplete,
	countFilledDateSlots,
	formatDate,
	formatDigitsToDisplayDate,
	formattedToDateSlots,
	getCursorAtDigitIndex,
	getDigitIndexBeforeCursor,
	parseDisplayDateStrict,
	resolveDisplayDateFromSlots,
	sanitizeDisplayDateInput,
	sanitizeDisplayDateRangeInput,
} from "utils/dates";

import calendarIMG from "img/icons/misc/calendar.svg";

import "./index.scss";

function DatepickerClearIcon() {
	return (
		<svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
			<path
				d="M18 6L6 18M6 6L18 18"
				fill="none"
				stroke="currentColor"
				strokeWidth={2}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

const Datepicker = (props) => {
	const {t} = useTranslation();
	const {
		onChange = () => {
		},
		isLimited = true,
		label = t("datepicker.dateRange"),
		prefix = <img
				src={calendarIMG}
				alt='icon calendar'
				style={{marginTop: 5}}
		/>,
		start,
		end,
		value,
		allowRange = true,
		placeholder,
		datepickerProps,
		isClearable = false,
		clearOnlyWhenHasValue = false,
		customFormatDate = formatDate,
		alignment = "left",
		labelDisabled,
		maxDatePeriodYear = 1,
		minDateOverride,
		maxDateOverride,
		disabled = false,
		requiredIndicator = false,
		error,
		toggleOnTriggerClick = false,
	} = props;

	const [active, setActive] = useState(false);
	const [renderDate, setRenderDate] = useState("");
	const [transactionDatetime, setTransactionDatetime] = useState(null);
	const [startDate, setStartDate] = useState(
			value ? new Date(value) : (start ? new Date(start) : minDateOverride ? new Date(minDateOverride) : null),
	);
	const [endDate, setEndDate] = useState(end ? new Date(end) : null);
	const [pickerViewDate, setPickerViewDate] = useState(null);
	const inputId = useId();
	const renderDateRef = useRef(renderDate);
	const allowRangeRef = useRef(allowRange);
	const startDateRef = useRef(startDate);
	const isInputFocusedRef = useRef(false);

	const pickerRef = useClickOutside(() => {
		setActive(false);
	});
	const { popoverRef, popoverStyle } = useDatepickerPopoverPosition({
		open: active && !disabled,
		preferredAlignment: alignment,
		anchorRef: pickerRef,
	});

	useEffect(() => {
		renderDateRef.current = renderDate;
	}, [renderDate]);

	useEffect(() => {
		allowRangeRef.current = allowRange;
	}, [allowRange]);

	useEffect(() => {
		startDateRef.current = startDate;
	}, [startDate]);

	useEffect(() => {
		if (startDate) {
			setPickerViewDate(startDate);
		}
	}, [startDate]);

	const restoreInputCursor = useCallback((formattedValue, digitIndex) => {
		requestAnimationFrame(() => {
			const input = document.getElementById(inputId);
			if (!input) {
				return;
			}

			const cursor = getCursorAtDigitIndex(
				formattedValue,
				Math.min(
					digitIndex,
					countFilledDateSlots(formattedToDateSlots(formattedValue)),
				),
			);
			input.setSelectionRange(cursor, cursor);
		});
	}, [inputId]);

	const commitFormattedInput = useCallback((formatted, cursorDigitIndex) => {
		setRenderDate(formatted);
		restoreInputCursor(formatted, cursorDigitIndex);

		if (!formatted) {
			setPickerViewDate(null);
			setStartDate(null);
			onChange(null);
			return;
		}

		const slots = formattedToDateSlots(formatted);
		const resolvedViewDate = resolveDisplayDateFromSlots(
			slots,
			startDateRef.current ?? pickerViewDate,
		);

		if (resolvedViewDate) {
			setPickerViewDate(resolvedViewDate);
		}

		if (areDateSlotsComplete(slots)) {
			const parsedDate = parseDisplayDateStrict(formatted);
			if (parsedDate) {
				setStartDate(parsedDate);
				setPickerViewDate(parsedDate);
				onChange(parsedDate);
			}
		}
	}, [onChange, pickerViewDate, restoreInputCursor]);

	const commitRangeFormattedInput = useCallback((sanitized) => {
		setRenderDate(sanitized);

		const [rangeStart, rangeEnd] = sanitized.split(" - ");
		if (rangeStart?.length === 10 && rangeEnd?.length === 10) {
			const parsedStart = parseDisplayDateStrict(rangeStart);
			const parsedEnd = parseDisplayDateStrict(rangeEnd);
			if (parsedStart && parsedEnd) {
				setStartDate(parsedStart);
				setEndDate(parsedEnd);
				onChange(parsedStart, parsedEnd);
			}
		}
	}, [onChange]);

	useEffect(() => {
		const input = document.getElementById(inputId);
		if (!input || disabled) {
			return undefined;
		}

		const isNavigationKey = (key) => (
			key === "ArrowLeft" ||
			key === "ArrowRight" ||
			key === "ArrowUp" ||
			key === "ArrowDown" ||
			key === "Home" ||
			key === "End" ||
			key === "Tab" ||
			key === "Enter" ||
			key === "Escape"
		);

		const handleKeyDown = (event) => {
			if (allowRangeRef.current) {
				return;
			}

			if (event.ctrlKey || event.metaKey || event.altKey) {
				return;
			}

			const currentValue = renderDateRef.current || "";
			const currentSlots = formattedToDateSlots(currentValue);
			const selectionStart = input.selectionStart ?? 0;
			const selectionEnd = input.selectionEnd ?? selectionStart;
			const startSlotIndex = getDigitIndexBeforeCursor(currentValue, selectionStart);
			const endSlotIndex = getDigitIndexBeforeCursor(currentValue, selectionEnd);

			if (/^\d$/.test(event.key)) {
				event.preventDefault();
				const replaceEndSlotIndex = endSlotIndex > startSlotIndex
					? endSlotIndex
					: (
						startSlotIndex < currentSlots.length &&
						currentSlots[startSlotIndex] !== ""
							? startSlotIndex + 1
							: startSlotIndex
					);
				const formatted = applyDateSlotEdit(currentValue, {
					startSlotIndex,
					endSlotIndex: replaceEndSlotIndex,
					insertDigits: event.key,
				});
				const nextCursor = Math.min(startSlotIndex + 1, 8);
				commitFormattedInput(formatted, nextCursor);
				return;
			}

			if (event.key === "Backspace") {
				event.preventDefault();
				const formatted = endSlotIndex > startSlotIndex
					? applyDateSlotEdit(currentValue, {
						startSlotIndex,
						endSlotIndex,
					})
					: applyDateSlotEdit(currentValue, {
						startSlotIndex: Math.max(0, startSlotIndex - 1),
						endSlotIndex: startSlotIndex,
					});
				const nextCursor = endSlotIndex > startSlotIndex
					? startSlotIndex
					: Math.max(0, startSlotIndex - 1);
				commitFormattedInput(formatted, nextCursor);
				return;
			}

			if (event.key === "Delete") {
				event.preventDefault();
				const formatted = endSlotIndex > startSlotIndex
					? applyDateSlotEdit(currentValue, {
						startSlotIndex,
						endSlotIndex,
					})
					: applyDateSlotEdit(currentValue, {
						startSlotIndex,
						endSlotIndex: Math.min(startSlotIndex + 1, 8),
					});
				const nextCursor = startSlotIndex;
				commitFormattedInput(formatted, nextCursor);
				return;
			}

			if (event.key.length === 1 && !isNavigationKey(event.key)) {
				event.preventDefault();
			}
		};

		const handlePaste = (event) => {
			if (allowRangeRef.current) {
				return;
			}

			event.preventDefault();
			const pastedText = event.clipboardData?.getData("text") ?? "";
			const pastedFormatted = sanitizeDisplayDateInput(pastedText);
			if (!pastedFormatted) {
				return;
			}

			const currentValue = renderDateRef.current || "";
			const selectionStart = input.selectionStart ?? 0;
			const selectionEnd = input.selectionEnd ?? selectionStart;
			const startSlotIndex = getDigitIndexBeforeCursor(currentValue, selectionStart);
			const endSlotIndex = getDigitIndexBeforeCursor(currentValue, selectionEnd);
			const formatted = applyDateSlotEdit(currentValue, {
				startSlotIndex,
				endSlotIndex,
				insertDigits: pastedFormatted.replace(/\D/g, ""),
			});
			const nextCursor = Math.min(
				startSlotIndex + pastedFormatted.replace(/\D/g, "").length,
				8,
			);
			commitFormattedInput(formatted, nextCursor);
		};

		input.addEventListener("keydown", handleKeyDown);
		input.addEventListener("paste", handlePaste);

		return () => {
			input.removeEventListener("keydown", handleKeyDown);
			input.removeEventListener("paste", handlePaste);
		};
	}, [commitFormattedInput, disabled, inputId]);

	useEffect(() => {
		if (value) {
			const parsedValue = new Date(value);
			if (!isNaN(parsedValue)) {
				setStartDate(parsedValue);
			}
		} else if (value === null || value === undefined) {
			// Reset startDate when value is explicitly null/undefined
			setStartDate(null);
		} else if (start) {
			const parsedStart = new Date(start);
			if (!isNaN(parsedStart)) {
				setStartDate(parsedStart);
			}
		}
	}, [value, start]);

	const minDate = minDateOverride
			? new Date(minDateOverride)
			: isLimited
					? new Date(new Date().setFullYear(new Date().getFullYear() - 5))
					: null;

	const maxDate = maxDateOverride
			? new Date(maxDateOverride)
			: isLimited
					? new Date(new Date().setFullYear(new Date().getFullYear() + maxDatePeriodYear))
					: null;


	useEffect(() => {
		if (allowRange) {
			if (start && end) {
				setRenderDate(`${customFormatDate(start)} - ${customFormatDate(end)}`);
			} else {
				setRenderDate("");
			}
		} else if (!isInputFocusedRef.current) {
			if (startDate) {
				setRenderDate(customFormatDate(startDate));
			} else if (value) {
				setRenderDate(customFormatDate(value));
			} else if (minDateOverride) {
				setRenderDate(customFormatDate(new Date(minDateOverride)));
			} else {
				setRenderDate(""); // Don't set default date, keep empty
			}
		}
	}, [allowRange, start, end, value, startDate, customFormatDate, minDateOverride]);

	const handleChange = (dates) => {
		if (!dates) {
			return;
		}

		if (allowRange) {
			const [rangeStart, rangeEnd] = dates;
			setStartDate(rangeStart);
			setEndDate(rangeEnd);
			onChange(rangeStart, rangeEnd);

			if (rangeStart && rangeEnd) {
				setRenderDate(
						`${customFormatDate(rangeStart)} - ${customFormatDate(rangeEnd)}`,
				);
			}
		} else {
			setStartDate(dates);
			setPickerViewDate(dates);
			onChange(dates);

			if (dates) {
				setRenderDate(customFormatDate(dates));
			}
		}

		setActive(false);
	};

	useEffect(() => {
		if (transactionDatetime) {
			const parsedDate = new Date(transactionDatetime);
			if (!isNaN(parsedDate)) {
				setStartDate(parsedDate);
			}
		}
	}, [transactionDatetime]);

	const handleDateChange = (startVal) => {
		if (startVal) {
			setTransactionDatetime(startVal.toISOString());
		}
	};

	const onClear = (e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		if (allowRange) {
			onChange(null, null);
		} else {
			onChange(null);
		}
		setRenderDate("");
		setStartDate(null);
		setEndDate(null);
		setActive(false);
	};

	const showClearControl =
		Boolean(isClearable) &&
		(!clearOnlyWhenHasValue || String(renderDate || "").trim() !== "");

	const handleToday = () => {
		const today = new Date();
		setStartDate(today);
		setEndDate(null);
		onChange(today);
		setRenderDate(customFormatDate(today));
		setActive(false);
	};

	const handleInputChange = (inputValue) => {
		if (allowRange) {
			const sanitized = sanitizeDisplayDateRangeInput(inputValue);
			commitRangeFormattedInput(sanitized);
			return;
		}

		// Fallback for autofill / IME — keyboard input is handled in keydown listener.
		const sanitized = formatDigitsToDisplayDate(inputValue);
		if (sanitized === (renderDateRef.current || "")) {
			return;
		}

		commitFormattedInput(
			sanitized,
			countFilledDateSlots(formattedToDateSlots(sanitized)),
		);
	};

	const handleInputBlur = () => {
		if (allowRange) {
			const [rangeStart, rangeEnd] = (renderDate || "").split(" - ");
			const isValidRange =
				rangeStart?.length === 10 &&
				rangeEnd?.length === 10 &&
				parseDisplayDateStrict(rangeStart) &&
				parseDisplayDateStrict(rangeEnd);

			if (!isValidRange) {
				if (startDate && endDate) {
					setRenderDate(
						`${customFormatDate(startDate)} - ${customFormatDate(endDate)}`,
					);
				} else {
					setRenderDate("");
				}
			}
			return;
		}

		const sanitized = sanitizeDisplayDateInput(renderDate);
		const parsedDate = parseDisplayDateStrict(sanitized);

		if (!parsedDate) {
			if (startDate) {
				setRenderDate(customFormatDate(startDate));
			} else {
				setRenderDate("");
			}
		}
	};

	const handleTriggerMouseDown = (e) => {
		if (disabled || !toggleOnTriggerClick) {
			return;
		}
		if (e.target.closest?.("[data-datepicker-clear]")) {
			return;
		}
		if (active) {
			e.preventDefault();
			setActive(false);
			return;
		}
		setActive(true);
	};

	return (
			<div
					className='Datepicker'
					ref={pickerRef}
			>
				<div
						className='Datepicker__trigger'
						onMouseDown={handleTriggerMouseDown}
				>
					<TextField
							id={inputId}
							type='text'
							label={labelDisabled ? "" : label}
							requiredIndicator={requiredIndicator}
							error={error}
							prefix={prefix}
							value={renderDate || ""}
							maxLength={allowRange ? 23 : 10}
							onFocus={() => {
								if (disabled) {
									return;
								}
								isInputFocusedRef.current = true;
								if (startDate) {
									setPickerViewDate(startDate);
								}
								if (!toggleOnTriggerClick) {
									setActive(true);
								}
							}}
							onBlur={() => {
								isInputFocusedRef.current = false;
								handleInputBlur();
							}}
							placeholder={placeholder}
							onChange={handleInputChange}
							autoComplete='off'
							disabled={disabled}
							suffix={
									showClearControl && (
											<button
													type='button'
													data-datepicker-clear
													className='Datepicker__clear'
													onMouseDown={(e) => {
														e.preventDefault();
														e.stopPropagation();
													}}
													onClick={onClear}
													aria-label='Clear'
													tabIndex={-1}
											>
												<DatepickerClearIcon />
											</button>
									)
							}
					/>
				</div>
				{active && !disabled && (
						<div
								ref={popoverRef}
								className='Datepicker-Wrapper'
								style={popoverStyle}
						>
							<ReactDatePicker
									selected={startDate}
									openToDate={pickerViewDate || startDate}
									onChange={handleChange}
									startDate={startDate}
									endDate={endDate}
									inline
									selectsRange={allowRange}
									showYearDropdown
									minDate={minDate}
									maxDate={maxDate}
									{...datepickerProps}
							/>
							<Button
									onClick={handleToday}
									variant='primary'
							>
								{t("Today")}
							</Button>
						</div>
				)}
			</div>
	);
};

Datepicker.propTypes = {
	onChange: PropTypes.func,
	label: PropTypes.string,
	prefix: PropTypes.node,
	start: PropTypes.string,
	end: PropTypes.string,
	value: PropTypes.string,
	allowRange: PropTypes.bool,
	placeholder: PropTypes.string,
	datepickerProps: PropTypes.object,
	isClearable: PropTypes.bool,
	clearOnlyWhenHasValue: PropTypes.bool,
	customFormatDate: PropTypes.func,
	alignment: PropTypes.oneOf(["right", "left"]),
	disabled: PropTypes.bool,
	requiredIndicator: PropTypes.bool,
	error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	toggleOnTriggerClick: PropTypes.bool,
};

export default Datepicker;
